import { PaginationState } from '@tanstack/react-table';
import classnames from 'classnames';
import React, { ReactNode, useEffect } from 'react';

import { LeftArrowIcon, RightArrowIcon } from '@icons';

import styles from '../Table.module.scss';
import { TablePaginationRef, useTableContext } from '../TableContext';

export type TablePaginationChangeHandler = (
  newState: PaginationState & { canNextPage: boolean; canPrevPage: boolean },
) => void;

export type TablePaginationProps = {
  onChange?: TablePaginationChangeHandler;
  getCanPreviousPage?: () => boolean;
  getCanNextPage?: () => boolean;
  previousPage?: () => void;
  nextPage?: () => void;
  setPageIndex?: (newIndex: number) => void;
  getPageCount?: () => number;
  getPageIndex?: () => number;
  lastPageHit?: boolean;
  hideNumbers?: boolean;
  speculativeNumbering?: boolean;
  leftDecorator?: ReactNode;
  rightDecorator?: ReactNode;
  lastEvaluatedKey?: string;
  onLastEvaluatedKeyChange?: (newKey?: string) => void;
  paginationData?: TablePaginationRef;
};

const MAX_VISIBLE_PAGE_NUMBERS = 7;

function TablePagination({
  onChange,
  getCanPreviousPage,
  previousPage,
  nextPage,
  getCanNextPage,
  setPageIndex,
  getPageIndex,
  getPageCount,
  speculativeNumbering,
  hideNumbers,
  leftDecorator,
  rightDecorator,
}: TablePaginationProps) {
  const { getTable, name: tableName } = useTableContext();

  const tableRef = getTable();

  const paginationState = tableRef?.getState()?.pagination ?? {
    pageIndex: 0,
    pageSize: 0,
  };

  const handlePrevious = () => {
    if (previousPage) return previousPage();
    return tableRef?.previousPage();
  };

  const handleNext = () => {
    if (nextPage) return nextPage();
    return tableRef?.nextPage();
  };

  const canPreviousPage = () => {
    if (getCanPreviousPage) return getCanPreviousPage();
    return tableRef?.getCanPreviousPage();
  };

  const canNextPage = () => {
    if (getCanNextPage) return getCanNextPage();
    return tableRef?.getCanNextPage();
  };

  const pageCount = () => {
    if (getPageCount) return getPageCount();
    return tableRef?.getPageCount();
  };

  const pageIndex = () => {
    if (getPageIndex) return getPageIndex();
    return +(paginationState?.pageIndex || 0);
  };

  const setPage = (pageIndex: number) => {
    if (setPageIndex) return setPageIndex(pageIndex);
    return tableRef?.setPageIndex(pageIndex);
  };

  const renderNumberButton = (index: number, currentPage: number) => {
    return (
      <button
        type="button"
        className={classnames(styles.numberButton, {
          [styles.selected]: index === currentPage,
        })}
        onClick={() => setPage(index)}
        key={`key-${index}`}
        name={`${tableName}-pagination-${index}`}
        aria-label={`${tableName}-pagination-${index}`}
        data-testid={`button-number-${index + 1}`}>
        {index + 1}
      </button>
    );
  };

  const renderSpeculativeNumbers = () => {
    const totalPages = pageCount() || 1;
    const currentPageIndex = pageIndex();
    const buttons = [];
    const finalPageIndex = totalPages - 1;
    let visiblePageNumbers;

    // Show correct amount of page numbers according to whether its also showing ellipses or not
    if (currentPageIndex <= 4) {
      visiblePageNumbers = MAX_VISIBLE_PAGE_NUMBERS;
    } else if (
      currentPageIndex > MAX_VISIBLE_PAGE_NUMBERS ||
      totalPages >= MAX_VISIBLE_PAGE_NUMBERS + 2
    ) {
      visiblePageNumbers = MAX_VISIBLE_PAGE_NUMBERS - 1;
    } else {
      visiblePageNumbers = MAX_VISIBLE_PAGE_NUMBERS;
    }

    const maxEitherSide = Math.floor(visiblePageNumbers / 2);

    // relative location of the first page
    let firstPageIndex = Math.max(
      0,
      Math.min(currentPageIndex - maxEitherSide, totalPages - visiblePageNumbers),
    );

    // firstPageIndex to center the current page to confirm to figma spec
    if (currentPageIndex > maxEitherSide && currentPageIndex < totalPages - maxEitherSide) {
      firstPageIndex = currentPageIndex - maxEitherSide;
    }

    const lastPageIndex = Math.min(finalPageIndex, firstPageIndex + visiblePageNumbers - 1);

    // first page index based on last page index
    if (lastPageIndex === finalPageIndex) {
      firstPageIndex = Math.max(0, finalPageIndex - visiblePageNumbers + 1);
    }

    // show first page and starting ellipses when required
    if (firstPageIndex > 0) {
      buttons.push(renderNumberButton(0, currentPageIndex));
      if (firstPageIndex > 1) {
        buttons.push(
          <span className={styles.ellipses} data-testid="start-ellipses" key="start-ellipses">
            {'...'}
          </span>,
        );
      }
    }

    for (let index = firstPageIndex; index <= lastPageIndex; index++) {
      buttons.push(renderNumberButton(index, currentPageIndex));
    }

    if (canNextPage()) {
      buttons.push(
        <span className={styles.ellipses} data-testid="end-ellipses" key="end-ellipses">
          {'...'}
        </span>,
      );
    }

    return buttons;
  };

  const renderNumbers = () => {
    if (speculativeNumbering) return renderSpeculativeNumbers();

    const totalPages = pageCount() || 1;
    const currentPage = pageIndex();
    const buttons = [];

    let startPage = Math.max(0, currentPage - 3);
    const endPage = Math.min(totalPages - 1, startPage + 7);

    if (endPage - startPage < 6) {
      startPage = Math.max(0, endPage - 6);
    }

    if (startPage > 0) {
      buttons.push(renderNumberButton(0, currentPage));
      if (startPage > 1) {
        buttons.push(
          <span className={styles.ellipses} data-testid="start-ellipses" key="start-ellipses">
            ...
          </span>,
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(renderNumberButton(i, currentPage));
    }

    if (endPage < totalPages - 1) {
      buttons.push(
        <span className={styles.ellipses} data-testid="end-ellipses" key="end-ellipses">
          ...
        </span>,
      );
    }

    return buttons;
  };

  useEffect(() => {
    if (onChange) {
      const event = {
        ...paginationState,
        canNextPage: canNextPage?.() || false,
        canPrevPage: canPreviousPage?.() || false,
      };
      onChange?.(event);
    }
  }, [paginationState]);

  return (
    <div className={styles.paginationContainer}>
      <div className={styles.leftDecorator}>{leftDecorator}</div>
      <div className={styles.pagination}>
        <button
          type="button"
          className={styles.caretButton}
          onClick={handlePrevious}
          disabled={!canPreviousPage()}
          aria-label={`${tableName}-pagination-previous`}
          name={`${tableName}-pagination-previous`}
          data-testid={'pagination-previous'}>
          <LeftArrowIcon size={32} />
        </button>
        {!hideNumbers && renderNumbers()}
        <button
          type="button"
          className={classnames(styles.caretButton, styles.right)}
          onClick={handleNext}
          disabled={!canNextPage()}
          aria-label={`${tableName}-pagination-next`}
          name={`${tableName}-pagination-next`}
          data-testid={'pagination-next'}>
          <RightArrowIcon size={32} />
        </button>
      </div>
      <div className={styles.rightDecorator}>{rightDecorator}</div>
    </div>
  );
}

export { TablePagination };
